import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { isPaymentEnabled } from 'app/core/environment/build-features';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { OverlayModule } from '@angular/cdk/overlay';
import { AppService } from 'app/core/app/app.service';
import {
  consumptionStatusColor,
  formatConsumptionAmount,
  formatCostCents,
} from 'app/core/consumption/consumption-format';
import { ConsumptionMeter } from 'app/core/consumption/consumption.model';
import { ConsumptionService } from 'app/core/consumption/consumption.service';
import { getProductDisplay } from 'app/core/payment/product-display';

/** Meters of one product, rendered under a shared caption in the panel. */
interface MeterGroup {
  slug: string;
  name: string;
  meters: ConsumptionMeter[];
}

/**
 * Compact consumption indicator for the application header bar.
 *
 * Driven entirely by {@link ConsumptionService}, it covers all activated
 * products (Voice, Rooms, Fleet, Cortex) of the active project:
 *
 * - On limited plans (free / mini / indie) the trigger shows a combined
 *   progress bar for the worst-case **consumed** fraction (filling left to
 *   right) across all limited meters.
 * - On pay-as-you-go it shows the estimated month-to-date cost instead.
 *
 * Clicking the indicator opens an overlay panel with per-product meter
 * rows and links to the "Usage & Costs" settings page. Projects with
 * external billing (or no meter data) hide the indicator entirely.
 */
@Component({
  selector: 'resource-meter',
  templateUrl: './resource-meter.component.html',
  styleUrls: ['./resource-meter.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule, OverlayModule],
})
export class ResourceMeterComponent {
  private _appService = inject(AppService);
  private _consumptionService = inject(ConsumptionService);
  private _router = inject(Router);

  /** Whether the detail overlay panel is currently visible */
  panelOpen = signal(false);

  /** The currently active app (used for the panel header and links) */
  activeApp = toSignal(this._appService.activeApp$, { initialValue: null });

  /** Consumption state (shared with the Usage & Costs settings panel) */
  readonly snapshot = this._consumptionService.snapshot;
  readonly mode = this._consumptionService.mode;
  readonly worstPercent = this._consumptionService.worstPercent;
  readonly totalCostCents = this._consumptionService.totalCostCents;

  /**
   * True on the builds that cannot reach Payment — staging and the local Fleet
   * builds. The indicator then renders as an inert placeholder rather than
   * vanishing, so the header has the same shape everywhere and nobody has to
   * remember that a control exists in production which is simply absent here.
   * Marked as a placeholder in the tooltip, because an empty bar that looks
   * measured is worse than no bar at all.
   */
  readonly placeholder = !isPaymentEnabled();

  /** Whether the header indicator should render at all */
  readonly visible = computed(() => {
    if (this.placeholder) {
      return true;
    }
    const snapshot = this.snapshot();
    if (!snapshot || snapshot.meters.length === 0) {
      return false;
    }
    if (snapshot.mode === 'payg') {
      return true;
    }
    // Quota plans need at least one limited meter to drive the trigger bar.
    return snapshot.mode === 'quota' && this.worstPercent() !== null;
  });

  /** Panel rows grouped by product, in fixed product order */
  readonly meterGroups = computed<MeterGroup[]>(() => {
    const meters = this.snapshot()?.meters ?? [];
    const knownOrder = ['voice', 'rooms', 'fleet', 'cortex'];
    const slugs = [
      ...knownOrder,
      ...meters
        .map((m) => m.productSlug)
        .filter((slug) => !knownOrder.includes(slug)),
    ];
    const groups: MeterGroup[] = [];
    for (const slug of slugs) {
      const group = meters.filter((m) => m.productSlug === slug);
      if (group.length > 0) {
        groups.push({
          slug,
          name: getProductDisplay(slug).name,
          meters: group,
        });
      }
    }
    return groups;
  });

  /** Toggle the detail overlay panel open/closed */
  togglePanel(): void {
    this.panelOpen.update((open) => !open);
  }

  /** Close the detail overlay panel */
  closePanel(): void {
    this.panelOpen.set(false);
  }

  /** Navigate to the Usage & Costs settings page for the active app */
  goToConsumption(): void {
    const app = this.activeApp();
    if (app) {
      this.closePanel();
      this._router.navigate(['/projects', app.id, 'settings', 'consumption']);
    }
  }

  /** Navigate to the plan settings page for the active app */
  goToSettings(): void {
    const app = this.activeApp();
    if (app) {
      this.closePanel();
      this._router.navigate(['/projects', app.id, 'settings', 'plan']);
    }
  }

  /**
   * Returns a CSS color class based on the consumption percentage.
   * Green for low usage, amber for moderate, red for critical.
   */
  getStatusColor(consumedPercent: number | null): string {
    return consumptionStatusColor(consumedPercent);
  }

  /** Row label, prefixed with the subtype when present (e.g. "M-4 · …") */
  displayLabel(meter: ConsumptionMeter): string {
    return meter.subtypeName
      ? `${meter.subtypeName} · ${meter.label}`
      : meter.label;
  }

  /**
   * Format a consumption amount for display.
   * Shows integers for whole numbers, one decimal for fractional.
   */
  formatAmount(value: number): string {
    return formatConsumptionAmount(value);
  }

  /** Format a cost in cents as a currency string (e.g. "12.34 €"). */
  formatCost(cents: number, currency = 'EUR'): string {
    return formatCostCents(cents, currency);
  }
}
